"use client"

import * as React from "react"
import { AppSidebar } from "./app-sidebar"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "./breadcrumb"
import { Separator } from "./separator"
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "./sidebar"

// Import the Patient Table component
import { 
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef
} from "@tanstack/react-table"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table'
import { Button } from './button'
import { Input } from './input'
import { Badge } from './badge'
import { Checkbox } from './checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'
import { AdminDashboardStats } from './admin-dashboard-stats'
import { PromptSelector, useSavedPrompts, type SavedPrompt } from './prompt-selector'
import { AvatarText } from './atomic/molecules/avatar-text'
import { ArrowUp, LayoutDashboard } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog'
import { Textarea } from './textarea'
import { Label } from './label'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// Import Supabase client
import { supabase } from '@joinomu/shared'

// Patient data type with relationship info
export type Patient = {
  id: string
  profile_id: string
  name: string
  careTeam: string[]
  treatments: string[]
  medications: string[]
  status: string[]
  treatmentType?: string
  assignedDate?: string
  isPrimary?: boolean
  email?: string
  hasCompletedIntake?: boolean
  // Additional properties for compatibility with patient-table
  first_name?: string
  last_name?: string
  created_at?: string
  phone?: string
  date_of_birth?: string
  has_completed_intake?: boolean
  assigned_providers?: string[]
  treatment_types?: string[]
  // Appointment data (same structure as provider dashboard)
  previous_appointment?: {
    date: string
    type: string
  }
  next_appointment?: {
    date: string
    type: string
  }
}

function TagList({ items, variant = "secondary" }: { items: string[], variant?: string }) {
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item, index) => (
        <Badge key={index} variant={variant as any} className="text-xs">
          {item}
        </Badge>
      ))}
    </div>
  )
}

function PatientTable({ 
  patients: initialPatients,
  isAdmin = false 
}: { 
  patients?: Patient[]
  isAdmin?: boolean 
}) {
  const [globalFilter, setGlobalFilter] = React.useState("")
  const [selectedRows, setSelectedRows] = React.useState<string[]>([])
  const [patients, setPatients] = React.useState<Patient[]>(initialPatients || [])
  const [loading, setLoading] = React.useState(!initialPatients)

  // Fetch all patients for admin view
  React.useEffect(() => {
    if (isAdmin && !initialPatients) {
      fetchAllPatients()
    }
  }, [isAdmin, initialPatients])

  const fetchAllPatients = async () => {
    try {
      // Get all patients with their provider relationships for admin view
      const { data: patientData, error } = await supabase
        .from('patients')
        .select(`
          id,
          profile_id,
          first_name,
          last_name,
          email,
          has_completed_intake,
          subscription_plan,
          subscription_status,
          current_period_end,
          stripe_customer_id,
          patient_assignments (
            treatment_type,
            is_primary,
            assigned_date,
            providers (
              first_name,
              last_name
            )
          )
        `)

      if (!error && patientData) {
        const transformedPatients: Patient[] = patientData.map((patient: any) => {
          // Extract provider info from relationships
          const providers: string[] = patient.patient_assignments?.map((pa: any) => 
            `Dr. ${pa.providers.first_name} ${pa.providers.last_name}`
          ) || []
          
          const treatments: string[] = patient.patient_assignments?.map((pa: any) => 
            pa.treatment_type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
          ) || []

          const status = patient.has_completed_intake ? ['Active'] : ['Onboarding']

          return {
            id: patient.id,
            profile_id: patient.profile_id || patient.id, // Use profile_id or fallback to id
            name: `${patient.first_name} ${patient.last_name}`,
            email: patient.email,
            careTeam: [...new Set(providers)], // Remove duplicates
            treatments: [...new Set(treatments)], // Remove duplicates
            medications: [] as string[], // Would need to fetch from medications table
            status,
            hasCompletedIntake: patient.has_completed_intake,
            // Add missing properties for compatibility
            first_name: patient.first_name,
            last_name: patient.last_name,
            created_at: patient.created_at || new Date().toISOString(),
            has_completed_intake: patient.has_completed_intake,
            assigned_providers: providers,
            treatment_types: treatments,
            // Subscription fields for billing display
            subscription_plan: patient.subscription_plan,
            subscription_status: patient.subscription_status,
            current_period_end: patient.current_period_end,
            stripe_customer_id: patient.stripe_customer_id
          }
        })
        
        setPatients(transformedPatients)
      }
    } catch (error) {
      console.error('Error fetching patients:', error)
    } finally {
      setLoading(false)
    }
  }

  // Handle individual row selection
  const handleRowSelect = (patientId: string, checked: boolean) => {
    if (checked) {
      setSelectedRows(prev => [...prev, patientId])
    } else {
      setSelectedRows(prev => prev.filter(id => id !== patientId))
    }
  }

  // Define table columns
  const columns = React.useMemo<ColumnDef<Patient>[]>(() => [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={selectedRows.length === table.getFilteredRowModel().rows.length && table.getFilteredRowModel().rows.length > 0}
          onCheckedChange={(checked) => {
            if (checked) {
              setSelectedRows(table.getFilteredRowModel().rows.map(row => row.original.id))
            } else {
              setSelectedRows([])
            }
          }}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedRows.includes(row.original.id)}
          onCheckedChange={(checked) => handleRowSelect(row.original.id, !!checked)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue("name")}</div>
      ),
    },
    {
      accessorKey: "careTeam",
      header: "Care Team",
      cell: ({ row }) => (
        <TagList
          items={row.getValue("careTeam")}
          variant="outline"
        />
      ),
    },
    {
      accessorKey: "treatments",
      header: "Treatments",
      cell: ({ row }) => (
        <TagList
          items={row.getValue("treatments")}
          variant="secondary"
        />
      ),
    },
    {
      accessorKey: "medications",
      header: "Medications",
      cell: ({ row }) => (
        <TagList
          items={row.getValue("medications")}
          variant="default"
        />
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <TagList
          items={row.getValue("status")}
          variant="outline"
        />
      ),
    },
  ], [selectedRows])

  const table = useReactTable({
    data: patients,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: "includesString",
    initialState: {
      pagination: {
        pageSize: 8,
      },
    },
  })

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="flex items-center justify-center h-64">
          <p>Loading patients...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full p-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between py-4">
        <Input
          placeholder="Search patients..."
          value={globalFilter ?? ""}
          onChange={(event) => setGlobalFilter(String(event.target.value))}
          className="max-w-sm"
        />
        
        <Button>
          Add Patient
        </Button>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="font-medium">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={`${selectedRows.includes(row.original.id) ? "bg-muted" : ""}`}
                  style={{ height: '21px' }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-1">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between space-x-2 py-4">
        <div className="text-sm text-muted-foreground">
          {selectedRows.length} of {table.getFilteredRowModel().rows.length} row(s) selected
        </div>
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value))
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="top">
                {[4, 8, 10, 20].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-[100px] items-center justify-center text-sm font-medium">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to first page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to previous page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Go to next page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Go to last page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Chat Input Component with Prompt Selector
function ChatInputWithState({ onSendMessage }: { onSendMessage: (message: string) => void }) {
  const [message, setMessage] = React.useState('')
  const { prompts, loading } = useSavedPrompts()

  const handlePromptSelect = (prompt: SavedPrompt) => {
    setMessage(prompt.prompt)
  }

  const handleSend = () => {
    if (message.trim()) {
      onSendMessage(message)
      setMessage('')
    }
  }

  return (
    <div className="fixed bottom-0 left-0 md:left-64 right-0 pt-4 px-4 pb-6 z-50 bg-background">
      <div className="flex flex-col bg-card rounded-[12px] p-2 shadow-md border">
        <div className="px-2 pt-2">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Ask anything..."
            className="w-full h-[100px] bg-transparent border-none outline-none text-foreground placeholder:text-muted-foreground resize-none"
            style={{ caretColor: '#C85A15' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                handleSend()
              }
            }}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <PromptSelector
            prompts={prompts}
            onSelectPrompt={handlePromptSelect}
            loading={loading}
          />
          <div className="flex-1"></div>
          <button
            type="button"
            onClick={handleSend}
            disabled={!message.trim()}
            className="p-2 hover:bg-muted rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowUp className="h-5 w-5 text-foreground" />
          </button>
        </div>
      </div>
    </div>
  )
}

// Save Prompt Dialog Component
function SavePromptDialog({ 
  isOpen, 
  onClose, 
  promptText,
  onSave
}: {
  isOpen: boolean
  onClose: () => void
  promptText: string
  onSave: (promptData: Partial<SavedPrompt>) => void
}) {
  const [title, setTitle] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [category, setCategory] = React.useState('general')
  const [tags, setTags] = React.useState('')
  const [isPublic, setIsPublic] = React.useState(false)
  const [editablePromptText, setEditablePromptText] = React.useState('')

  // Initialize editable prompt text when dialog opens
  React.useEffect(() => {
    if (isOpen && promptText) {
      setEditablePromptText(promptText)
    }
  }, [isOpen, promptText])

  const handleSave = () => {
    const promptData = {
      title,
      description,
      category,
      tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0),
      prompt: editablePromptText,
      is_public: isPublic,
      created_at: new Date().toISOString(),
      id: Date.now().toString(),
      usage_count: 0
    }
    
    onSave(promptData)
    onClose()
    
    // Reset form
    setTitle('')
    setDescription('')
    setCategory('general')
    setTags('')
    setIsPublic(false)
    setEditablePromptText('')
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save Prompt</DialogTitle>
          <DialogDescription>
            Save this message as a reusable prompt template.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter prompt title"
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this prompt does"
              rows={2}
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="patient_analysis">Patient Analysis</SelectItem>
                <SelectItem value="medication_review">Medication Review</SelectItem>
                <SelectItem value="care_planning">Care Planning</SelectItem>
                <SelectItem value="risk_assessment">Risk Assessment</SelectItem>
                <SelectItem value="administrative">Administrative</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="tags">Tags</Label>
            <Input
              id="tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="patient, analysis, summary (comma-separated)"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isPublic"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="h-4 w-4"
            />
            <Label htmlFor="isPublic">Make this prompt public</Label>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="promptText">Prompt Text</Label>
            <Textarea
              id="promptText"
              value={editablePromptText}
              onChange={(e) => setEditablePromptText(e.target.value)}
              placeholder="Enter or edit the prompt text"
              rows={4}
              className="min-h-[100px]"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!title.trim()}>
            Save Prompt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function AdminDashboard({
  user,
  onLogout,
  activeItem = "Dashboard",
  showPatientTable = false,
  onNavigate,
  onAccountClick,
  onSupportFeedbackClick,
  patients
}: {
  user?: {
    name: string
    email: string
    avatar?: string
  }
  onLogout?: () => void
  activeItem?: string
  showPatientTable?: boolean
  onNavigate?: (item: string) => void
  onAccountClick?: () => void
  onSupportFeedbackClick?: (type: "Support" | "Feedback") => void
  patients?: Patient[]
}) {
  const [chatMessages, setChatMessages] = React.useState<Array<{ user: string; assistant: string; contextData?: any }>>([])
  const [showDashboard, setShowDashboard] = React.useState(true)
  const [showSaveDialog, setShowSaveDialog] = React.useState(false)
  const [promptToSave, setPromptToSave] = React.useState('')

  const handleSendMessage = async (message: string) => {
    // Add user message immediately
    const userMessage = { user: message, assistant: '' }
    setChatMessages(prev => [...prev, userMessage])
    setShowDashboard(false)

    try {
      // Build conversation history for context
      const conversationHistory = chatMessages.map(msg => ({
        user: msg.user,
        assistant: msg.assistant
      }))

      // Call real LLM API with conversation history
      const response = await fetch('/api/admin/llm-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          conversationHistory,
          provider: 'anthropic'
        })
      })

      if (!response.ok) {
        throw new Error('Failed to get LLM response')
      }

      const result = await response.json()
      
      // Update the last message with the assistant response
      setChatMessages(prev => {
        const updated = [...prev]
        const lastIndex = updated.length - 1
        if (lastIndex >= 0) {
          updated[lastIndex] = {
            user: message,
            assistant: result.response,
            contextData: result.contextData
          }
        }
        return updated
      })
      
    } catch (error) {
      console.error('Error getting LLM response:', error)
      
      // Update with error message
      setChatMessages(prev => {
        const updated = [...prev]
        const lastIndex = updated.length - 1
        if (lastIndex >= 0) {
          updated[lastIndex] = {
            user: message,
            assistant: 'Sorry, I encountered an error processing your request. Please try again or contact support if the problem persists.'
          }
        }
        return updated
      })
    }
  }

  const handleResetChat = () => {
    setChatMessages([])
    setShowDashboard(true)
  }

  const handleSavePrompt = (text: string) => {
    setPromptToSave(text)
    setShowSaveDialog(true)
  }

  const handlePromptSaved = async (promptData: Partial<SavedPrompt>) => {
    try {
      const response = await fetch('/api/admin/prompts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(promptData)
      })

      if (!response.ok) {
        throw new Error('Failed to save prompt')
      }

      const savedPrompt = await response.json()
      console.log('Prompt saved successfully:', savedPrompt)
      
      setShowSaveDialog(false)
      setPromptToSave('')
    } catch (error) {
      console.error('Error saving prompt:', error)
    }
  }

  // Sample admin user for AvatarText
  const adminUser = {
    name: user?.name || 'Admin User',
    email: user?.email || 'admin@joinomu.com',
    avatar: user?.avatar || ''
  }

  return (
    <SidebarProvider>
        <AppSidebar 
          user={user} 
          onLogout={onLogout} 
          onNavigate={onNavigate} 
          onAccountClick={onAccountClick} 
          onSupportFeedbackClick={onSupportFeedbackClick}
          userRole="admin" 
        />
        <SidebarInset>
          <header className="flex h-16 shrink-0 items-center gap-2">
            <div className="flex items-center gap-2 px-4">
              <SidebarTrigger className="-ml-1" />
              <Separator
                orientation="vertical"
                className="mr-2 data-[orientation=vertical]:h-4"
              />
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbPage>Admin {activeItem}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
              
              {/* Dashboard Reset Button - only show when in chat mode */}
              {!showDashboard && (
                <button
                  onClick={handleResetChat}
                  className="ml-4 p-1 hover:bg-gray-100 rounded"
                  title="Return to Dashboard"
                >
                  <LayoutDashboard className="h-4 w-4" />
                </button>
              )}
            </div>
          </header>
          
          <div className="flex flex-1 flex-col gap-4 p-4 pt-0" style={{ paddingBottom: showDashboard ? '0' : '200px' }}>
            {showDashboard ? (
              <>
                {showPatientTable ? (
                  <PatientTable patients={patients} isAdmin={true} />
                ) : (
                  <>
                    {/* Dashboard Stats Cards */}
                    <AdminDashboardStats />
                  </>
                )}
              </>
            ) : (
              <div className="space-y-4">
                {chatMessages.map((msg, index) => (
                  <div key={index} className="space-y-3">
                    {/* User message with AvatarText - right aligned */}
                    <div className="flex justify-end">
                      <div style={{ maxWidth: '80%' }}>
                        <AvatarText
                          user={adminUser}
                          text={msg.user}
                          onSavePrompt={handleSavePrompt}
                        />
                      </div>
                    </div>
                    
                    {/* Assistant response - left aligned, markdown rendered */}
                    {msg.assistant && (
                      <div className="flex justify-start">
                        <div className="max-w-[80%] w-full">
                          <div className="p-4 rounded-lg bg-muted text-sm text-foreground prose prose-sm dark:prose-invert max-w-none
                            prose-p:my-2 prose-p:leading-relaxed
                            prose-headings:mt-4 prose-headings:mb-2 prose-headings:font-semibold
                            prose-h1:text-lg prose-h2:text-base prose-h3:text-sm
                            prose-ul:my-2 prose-ul:pl-4 prose-ol:my-2 prose-ol:pl-4
                            prose-li:my-0.5
                            prose-code:bg-background prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:before:content-none prose-code:after:content-none
                            prose-pre:bg-background prose-pre:p-3 prose-pre:rounded-md prose-pre:overflow-x-auto
                            prose-table:text-xs prose-th:p-2 prose-td:p-2 prose-th:border prose-td:border
                            prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                            prose-strong:font-semibold prose-em:italic
                            prose-blockquote:border-l-2 prose-blockquote:border-muted-foreground prose-blockquote:pl-4 prose-blockquote:italic">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {msg.assistant}
                            </ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </SidebarInset>
        
        {/* Chat Input - always visible for admin LLM assistant */}
        <ChatInputWithState onSendMessage={handleSendMessage} />

        {/* Save Prompt Dialog */}
        <SavePromptDialog
          isOpen={showSaveDialog}
          onClose={() => setShowSaveDialog(false)}
          promptText={promptToSave}
          onSave={handlePromptSaved}
        />
    </SidebarProvider>
  )
}